import {isValueNumericEnum} from '@colonist/validation'
import {type NonFunctionProperties} from '@colonist/types'
import {BotSpeedSettings, DiceSettingEnum, ELOTypeEnum, ExtensionSettingEnum, GameSpeedSettings, GameTypeEnum, MapSettingEnum, ModeSettingEnum, RoomSettingConstants, ScenarioSettingEnum} from '@colonist/game-base-types'

export class GameSettings {
    // General
    id: string
    channelId?: string | undefined
    gameType: GameTypeEnum
    privateGame: boolean
    playOrderSelectionActive: boolean
    minimumKarma: number = RoomSettingConstants.DEFAULT_KARMA_THRESHOLD
    eloType: ELOTypeEnum

    // Tested General
    modeSetting: ModeSettingEnum = RoomSettingConstants.DEFAULT_MODE_SETTING
    extensionSetting: ExtensionSettingEnum = RoomSettingConstants.DEFAULT_EXTENSION_SETTING
    scenarioSetting: ScenarioSettingEnum = RoomSettingConstants.DEFAULT_SCENARIO_SETTING
    mapSetting: MapSettingEnum = RoomSettingConstants.DEFAULT_MAP_SETTING
    diceSetting: DiceSettingEnum = RoomSettingConstants.DEFAULT_DICE_SETTING

    // Variables
    victoryPointsToWin: number = RoomSettingConstants.DEFAULT_VP_TO_WIN
    karmaActive: boolean = true
    cardDiscardLimit: number = RoomSettingConstants.DEFAULT_CARD_DISCARD_LIMIT
    maxPlayers: number = RoomSettingConstants.DEFAULT_PLAYER_COUNT
    gameSpeed: GameSpeedSettings = RoomSettingConstants.DEFAULT_GAME_SPEED_SETTING
    botSpeed: BotSpeedSettings = RoomSettingConstants.DEFAULT_BOT_SPEED_SETTING
    hideBankCards: boolean = RoomSettingConstants.DEFAULT_HIDE_BANK_CARDS
    friendlyRobber: boolean = RoomSettingConstants.DEFAULT_FRIENDLY_ROBBER

    constructor(id: string, gameType: GameTypeEnum) {
        this.id = id
        this.gameType = gameType
        this.privateGame = false
        this.playOrderSelectionActive = false
        this.eloType = ELOTypeEnum.Inactive
    }

    static deserialize(data: NonFunctionProperties<GameSettings>): GameSettings {
        const setting = new GameSettings(data.id, data.gameType)

        setting.channelId = data.channelId

        setting.privateGame = data.privateGame
        setting.minimumKarma = data.minimumKarma
        setting.eloType = data.eloType

        setting.modeSetting = data.modeSetting
        setting.mapSetting = data.mapSetting

        setting.victoryPointsToWin = data.victoryPointsToWin
        setting.karmaActive = data.karmaActive
        setting.cardDiscardLimit = data.cardDiscardLimit
        setting.maxPlayers = data.maxPlayers
        setting.gameSpeed = data.gameSpeed
        setting.botSpeed = data.botSpeed
        setting.hideBankCards = data.hideBankCards
        setting.friendlyRobber = data.friendlyRobber
        setting.diceSetting = data.diceSetting

        setting.scenarioSetting = data.scenarioSetting
        setting.extensionSetting = data.extensionSetting

        setting.gameType = data.gameType
        setting.playOrderSelectionActive = data.playOrderSelectionActive

        return setting
    }

    isValidGameSettings(): boolean {
        for(const attribute of Object.keys(this)) {
            if(!this.isValidDataForAttribute(attribute)) return false
        }

        return true
    }

    isNoTrollsGame(): boolean {
        return this.minimumKarma == RoomSettingConstants.NO_TROLLS_KARMA_THRESHOLD
    }

    toggleNoTrolls() {
        this.minimumKarma = this.isNoTrollsGame() ? RoomSettingConstants.DEFAULT_KARMA_THRESHOLD : RoomSettingConstants.NO_TROLLS_KARMA_THRESHOLD
    }

    private isValidDataForAttribute(attribute: string): boolean {
        const isAttributeMemberOfClass = attribute in this
        if(!isAttributeMemberOfClass) return false

        const data = this[attribute]
        switch(attribute) {
            case 'id': return typeof data === 'string'
            case 'channelId': return typeof data === 'string' || data == undefined
            case 'name': return typeof data === 'string'
            case 'privateGame': return typeof data === 'boolean'
            case 'playOrderSelectionActive': return typeof data === 'boolean'
            case 'minimumKarma': return typeof data === 'number'
            case 'modeSetting': return isValueNumericEnum(data, ModeSettingEnum)
            case 'extensionSetting': return isValueNumericEnum(data, ExtensionSettingEnum)
            case 'scenarioSetting': return isValueNumericEnum(data, ScenarioSettingEnum)
            case 'mapSetting': return isValueNumericEnum(data, MapSettingEnum)
            case 'diceSetting': return isValueNumericEnum(data, DiceSettingEnum)
            case 'victoryPointsToWin': return typeof data === 'number'
            case 'karmaActive': return typeof data === 'boolean'
            case 'cardDiscardLimit': return typeof data === 'number'
            case 'maxPlayers': return typeof data === 'number'
            case 'gameSpeed': return isValueNumericEnum(data, GameSpeedSettings)
            case 'botSpeed': return isValueNumericEnum(data, BotSpeedSettings)
            case 'hideBankCards': return typeof data === 'boolean'
            case 'friendlyRobber': return typeof data === 'boolean'
            case 'gameType': return isValueNumericEnum(data, GameTypeEnum)
            case 'eloType': return isValueNumericEnum(data, ELOTypeEnum)
        }

        return false
    }

    isSimilarTo(other: GameSettings) {
        return (this.modeSetting === other.modeSetting)
            && (this.extensionSetting === other.extensionSetting)
            && (this.scenarioSetting === other.scenarioSetting)
            && (this.mapSetting === other.mapSetting)
            && (this.diceSetting === other.diceSetting)
    }
}

/*
 Future:

 elo limit
 expansion pack
 enableSpectators
 ranked
 select player colors
 fill with bots
 robber can't return to the deserts
 sound effects
 auto-reject bot trades after 6 seconds
 aitSeconds multiplier

 largestArmySize: number = 3
 longestRoadLength: number = 5
 largestArmyCardPoints: number = 2
 longestRoadCardPoints: number = 2

 ===================================
 Possible Others:

 BankController: max resource counts? max dev card counts?
 PlayerController: max road, settlement, city counts?
 playerorder: shuffle? have a set order?
 get resources from both settlements in game placement?
 max time for trade?
 max amounts of trades per turn?
 bot difficulty?

 */
